/**
 * Who has looked at my record.
 *
 * §12.4: "Audit records are queryable by the privacy officer and, per policy,
 * by the PATIENT — an access report is a product feature, not an afterthought."
 * This is the patient half of that sentence, and the reason the audit ledger
 * refuses any write it cannot record: every row here exists because some
 * access was allowed to happen only after it was written down (§1.5).
 *
 * It is deliberately unfiltered and unflattering. Every read is listed,
 * including reads by services and by this portal itself. A page that quietly
 * omitted the boring rows would be a page that could quietly omit an
 * interesting one, and the whole value of an access report is that a patient
 * can believe it.
 */

import { type JSX, useMemo } from 'react';

import { Async } from '../components/Async.js';
import { useAccessReport } from '../lib/queries.js';

interface AuditRow {
  readonly recorded: string;
  readonly action: string;
  readonly who: string;
  readonly purpose: string;
}

interface RawAuditEvent {
  readonly recorded?: string;
  readonly action?: string;
  readonly agent?: {
    readonly requestor?: boolean;
    readonly who?: { readonly reference?: string };
  }[];
  readonly purposeOfEvent?: { readonly coding?: { readonly code?: string }[] }[];
}

/**
 * FHIR's single-letter `AuditEvent.action`, spelled out.
 *
 * Not a clinical code list — these are FHIR's own fixed action codes
 * (`http://hl7.org/fhir/audit-event-action`), a closed set of five defined by
 * the base spec, not terminology resolved from `ig/`. §8.0.1 is about clinical
 * codes; rendering "C" as "Created" is a translation of the wire format.
 */
const ACTION_WORDS: Readonly<Record<string, string>> = {
  C: 'Added something',
  R: 'Looked at it',
  U: 'Changed something',
  D: 'Deleted something',
  E: 'Ran a search',
};

export const WhoHasSeen = ({ patientId }: { readonly patientId: string }): JSX.Element => {
  const report = useAccessReport(patientId);

  const rows = useMemo<readonly AuditRow[]>(
    () =>
      (report.data ?? []).map((entry): AuditRow => {
        const event = entry as RawAuditEvent;
        return {
          recorded: event.recorded ?? 'unknown',
          action: ACTION_WORDS[event.action ?? ''] ?? event.action ?? 'unknown',
          // The requestor is the actor who caused the access, as distinct from
          // the services that carried it out.
          who: event.agent?.find((agent) => agent.requestor === true)?.who?.reference ?? 'unknown',
          purpose: event.purposeOfEvent?.[0]?.coding?.[0]?.code ?? 'unknown',
        };
      }),
    [report.data],
  );

  return (
    <section aria-labelledby="access-heading">
      <h1 id="access-heading">Who has seen my record</h1>

      <p>
        Every time someone opens your record, it is written down here. This includes your care team,
        this website, and any system acting on your behalf.
      </p>

      <Async query={report} empty="Nobody has opened your record yet.">
        {() => (
          <table data-testid="access-report">
            <caption>{rows.length} access event(s), most recent first</caption>
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Who</th>
                <th scope="col">What they did</th>
                <th scope="col">Why</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={`${row.recorded}-${index}`}>
                  <td>{row.recorded}</td>
                  <td>{row.who}</td>
                  <td>{row.action}</td>
                  <td>{row.purpose}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Async>

      <p className="notice" role="note">
        If you see access you do not recognise, contact your care team or the privacy officer. This
        list cannot be edited or deleted, by anyone — that is what makes it worth reading.
      </p>
    </section>
  );
};
